import React, { useEffect, useRef, useState } from 'react';
import { api, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileText, CheckCircle2, XCircle, RotateCcw, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  submitted: { label: 'Awaiting Review', color: 'gold', icon: Clock },
  approved: { label: 'Approved', color: 'green', icon: CheckCircle2 },
  revision_required: { label: 'Revision Required', color: 'amber', icon: RotateCcw },
  rejected: { label: 'Rejected', color: 'red', icon: XCircle },
  superseded: { label: 'Superseded', color: 'slate', icon: FileText },
};

const COLOR = {
  gold: 'bg-gold/10 text-gold',
  green: 'bg-trjgreen/10 text-trjgreen',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  slate: 'bg-slate-100 text-slate-600',
};

export default function SubmissionLesson({ courseId, lesson, onReload }) {
  const [submissions, setSubmissions] = useState([]);
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get('/submissions/me');
    setSubmissions(data.filter(s => s.lesson_id === lesson.lesson_id && s.course_id === courseId));
  };
  useEffect(() => { load(); }, [lesson.lesson_id]); // eslint-disable-line

  const active = submissions.find(s => ['submitted', 'approved', 'revision_required'].includes(s.status));
  const history = submissions;

  const submit = async () => {
    if (!file) { toast.error('Please choose a file'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('course_id', courseId);
    fd.append('lesson_id', lesson.lesson_id);
    fd.append('note', note);
    setBusy(true);
    try {
      const token = localStorage.getItem('trj_access_token');
      const res = await fetch(`${API_BASE}/submissions/upload`, {
        method: 'POST', body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Upload failed');
      }
      toast.success('Submission uploaded for review.');
      setFile(null); setNote('');
      if (inputRef.current) inputRef.current.value = '';
      await load();
      onReload && onReload();
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  const kindLabel = lesson.submission_kind === 'capstone' ? 'Capstone Project'
                   : lesson.submission_kind === 'business_plan' ? 'Business Launch Plan'
                   : 'Assignment';

  return (
    <div data-testid={`submission-lesson-${lesson.lesson_id}`}>
      <div className="text-xs uppercase tracking-widest text-gold font-semibold">Submission · {kindLabel}</div>
      <h2 className="font-display font-semibold text-2xl text-navy mt-2">{lesson.title}</h2>
      <div className="mt-4 text-slate-700 leading-relaxed">{lesson.body}</div>

      {active && (
        <div className="mt-6 border border-slate-200 rounded-md p-5 bg-slate-50" data-testid="active-submission">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <StatusBadge status={active.status} />
              <div className="text-sm text-slate-700 font-medium">{active.filename}</div>
            </div>
            <div className="text-xs text-slate-500">Submitted {new Date(active.submitted_at).toLocaleString()}</div>
          </div>
          {active.review && (
            <div className="mt-4 bg-white rounded-md p-4 border border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Faculty review</div>
              <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{active.review.feedback || '(no feedback provided)'}</div>
              {active.review.grade && <div className="mt-2 text-xs text-slate-500">Grade: <span className="text-navy font-semibold">{active.review.grade}</span></div>}
              <div className="mt-2 text-xs text-slate-400">Reviewed by {active.review.reviewed_by} · {new Date(active.review.reviewed_at).toLocaleString()}</div>
            </div>
          )}
        </div>
      )}

      {(!active || active.status === 'revision_required') && (
        <div className="mt-6 border-2 border-dashed border-slate-300 rounded-lg p-6" data-testid="submission-uploader">
          <div className="flex items-center gap-3">
            <Upload className="h-6 w-6 text-navy" />
            <div>
              <div className="font-display font-semibold text-navy">{active ? 'Upload revision' : 'Upload your submission'}</div>
              <div className="text-xs text-slate-500">PDF, DOC, DOCX, XLSX, ZIP — max 25 MB</div>
            </div>
          </div>
          <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.png,.jpg,.jpeg"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="submission-file-input"
            className="mt-4 block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-navy file:text-white hover:file:bg-navy-600" />
          <div className="mt-3">
            <Textarea rows={3} placeholder="Optional note to faculty…" value={note}
              onChange={(e) => setNote(e.target.value)} data-testid="submission-note" />
          </div>
          <Button onClick={submit} disabled={busy || !file} className="btn-gold mt-4" data-testid="submission-submit-btn">
            <Upload className="h-4 w-4 mr-2" /> {busy ? 'Uploading…' : 'Submit for Review'}
          </Button>
        </div>
      )}

      {history.length > 1 && (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Submission history</div>
          <div className="space-y-2">
            {history.map((s) => (
              <div key={s.submission_id} className="flex items-center justify-between text-sm border border-slate-200 rounded-md p-3">
                <div className="flex items-center gap-3">
                  <StatusBadge status={s.status} />
                  <span className="text-slate-700">{s.filename}</span>
                </div>
                <div className="text-xs text-slate-400">{new Date(s.submitted_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.submitted;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded ${COLOR[meta.color]}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}
